import { z } from 'zod';
import operatorApiFetch from './apiService';
import { apiSuccess, apiList, parseApiResponse } from '@services/apiSchemas';

// ── Per-endpoint schemas ────────────────────────────────────────────────

// Api::Operator::V1::WorkOrdersController#format_work_order_index (lines 249-263)
const WorkOrderListRowSchema = z.object({
  id: z.string(),
  barcode: z.string().nullable(),
  name: z.string(),
  merchant_id: z.string(),
  merchant_name: z.string().nullable(),
  warehouse_id: z.string(),
  warehouse_name: z.string().nullable(),
  status: z.string(),
  scheduled_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  created_at: z.string(),
}).passthrough();

// Item shape inside format_work_order_show (lines 279-296)
const WorkOrderItemSchema = z.object({
  id: z.string(),
  product_id: z.string(),
  product_name: z.string().nullable(),
  product_sku: z.string().nullable(),
  product_barcode: z.string().nullable(),
  // DB: work_order_items.inventory_type_id uuid NOT NULL — never null on read.
  inventory_type_id: z.string(),
  inventory_type_name: z.string().nullable(),
  role: z.enum(['input', 'output']),
  quantity: z.number(),
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
  manufacturing_date: z.string().nullable(),
  note: z.string().nullable(),
  custom_attributes: z.record(z.string(), z.unknown()).nullable(),
}).passthrough();

// Fulfillment shape (lines 297-311)
const WorkOrderFulfillmentItemSchema = z.object({
  id: z.string(),
  product_name: z.string().nullable(),
  quantity: z.number(),
  status: z.string(),
}).passthrough();

const WorkOrderFulfillmentSchema = z.object({
  id: z.string(),
  number: z.string(),
  status: z.string(),
  items: z.array(WorkOrderFulfillmentItemSchema),
}).passthrough();

// PickingList shape (lines 312-320)
const WorkOrderPickingListSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  tote_id: z.string().nullable(),
  tote_code: z.string().nullable(),
}).passthrough();

// Api::Operator::V1::WorkOrdersController#suggest_name — returns { name }.
const WorkOrderSuggestedNameSchema = z.object({
  name: z.string(),
}).passthrough();

// Full show response
const WorkOrderDetailSchema = z.object({
  id: z.string(),
  barcode: z.string().nullable(),
  name: z.string(),
  status: z.string(),
  merchant_id: z.string(),
  merchant_name: z.string().nullable(),
  warehouse_id: z.string(),
  warehouse_name: z.string().nullable(),
  scheduled_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
  items: z.array(WorkOrderItemSchema),
  fulfillments: z.array(WorkOrderFulfillmentSchema),
  picking_lists: z.array(WorkOrderPickingListSchema),
  has_picking_list: z.boolean(),
  picking_completed: z.boolean(),
  production_inbound_id: z.string().nullable(),
}).passthrough();

// ── Service ─────────────────────────────────────────────────────────────

export const workOrderService = {
  getRecords: async (params: Record<string, unknown> = {}) => {
    const res = await operatorApiFetch('work_orders/get_records', {
      method: 'POST',
      body: params,
    });
    return parseApiResponse(res, apiList(WorkOrderListRowSchema));
  },

  get: async (id: string) => {
    const res = await operatorApiFetch(`work_orders/${id}`, {
      method: 'GET',
    });
    return parseApiResponse(res, apiSuccess(WorkOrderDetailSchema));
  },

  create: async (data: Record<string, unknown>) => {
    const res = await operatorApiFetch('work_orders', {
      method: 'POST',
      body: { work_order: data },
    });
    return parseApiResponse(res, apiSuccess(WorkOrderDetailSchema));
  },

  update: async (id: string, data: Record<string, unknown>) => {
    const res = await operatorApiFetch(`work_orders/${id}`, {
      method: 'PUT',
      body: { work_order: data },
    });
    return parseApiResponse(res, apiSuccess(WorkOrderDetailSchema));
  },

  confirm: async (id: string) => {
    const res = await operatorApiFetch(`work_orders/${id}/confirm`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(WorkOrderDetailSchema));
  },

  complete: async (id: string, data: Record<string, unknown> = {}) => {
    const res = await operatorApiFetch(`work_orders/${id}/complete`, {
      method: 'POST',
      body: data,
    });
    return parseApiResponse(res, apiSuccess(WorkOrderDetailSchema));
  },

  createPickingList: async (id: string) => {
    const res = await operatorApiFetch(`work_orders/${id}/create_picking_list`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(WorkOrderDetailSchema));
  },

  delete: async (id: string) => {
    const res = await operatorApiFetch(`work_orders/${id}`, {
      method: 'DELETE',
    });
    // destroy renders `render_data_item(nil, ...)` → data is null
    return parseApiResponse(res, apiSuccess(z.null()));
  },

  // BE requires merchant_id (current_operator_merchant resolves from
  // params[:merchant_id]); WorkOrderForm always has a selected merchant so
  // the param is mandatory at the service boundary.
  suggestName: async (merchantId: string) => {
    const res = await operatorApiFetch(
      `work_orders/suggest_name?merchant_id=${encodeURIComponent(merchantId)}`,
      { method: 'GET' },
    );
    return parseApiResponse(res, apiSuccess(WorkOrderSuggestedNameSchema));
  },
};

export default workOrderService;

// z.infer types for callers
export type WorkOrderListRow = z.infer<typeof WorkOrderListRowSchema>;
export type WorkOrderDetail = z.infer<typeof WorkOrderDetailSchema>;
export type WorkOrderItem = z.infer<typeof WorkOrderItemSchema>;
